import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { cn } from "@/shared/lib/utils";

const SIZE_PX: Record<"small" | "default" | "large", number> = {
  small: 28,
  default: 40,
  large: 56,
};

interface SpinnerProps {
  size?: "small" | "default" | "large";
  className?: string;
}

/** The platform's one loading indicator — AntD's Spin with a larger LoadingOutlined
 * icon in brand red, instead of the default dot spinner, everywhere the app shows
 * a "this is loading" state. */
export function Spinner({ size = "default", className }: SpinnerProps) {
  return (
    <Spin
      indicator={<LoadingOutlined style={{ fontSize: SIZE_PX[size], color: "#E4002B" }} spin />}
      className={className}
    />
  );
}

interface PageSpinnerProps extends SpinnerProps {
  /** Use before the portal layout (sidebar/header) has mounted — centers in the
   * full browser viewport instead of just the space below the header. */
  fullScreen?: boolean;
}

// Every portal header is a fixed 73px (see PatientHeader/PortalHeader) — content-area
// loading states center against that real viewport height instead of `min-h-full`,
// which silently collapses to the spinner's own size whenever an ancestor's resolved
// height is "auto" rather than a percentage-resolvable value.
export function PageSpinner({ size = "large", className, fullScreen = false }: PageSpinnerProps) {
  return (
    <div
      className={cn(
        "flex w-full items-center justify-center",
        fullScreen ? "h-screen" : "min-h-[calc(100vh-73px)]",
        className,
      )}
    >
      <Spinner size={size} />
    </div>
  );
}
